import { FETCH_QUERIES_NAME, NULL_OPEN_PORTAL_EVENT } from '@common/constants';
import { Button, ComponentType, ModalBodyExtras, useForm, useModal } from '@gcl/react-core';
import { useNavigate } from '@routes/routes.hooks';
import { useQueryClient } from '@tanstack/react-query';
import { ETransmittalType, TransmittalTypeOptions } from '@transmittal/transmittal.constants';
import { ICreateProjectTransmittalRequest } from '@transmittal/transmittal.models';
import { useCreateDraftProjectTransmittalIfNotExist } from '@transmittal/transmittal.queries';
import * as React from 'react';
import { getTransmittalItemRoute } from '@transmittal/detailPage/transmittalDetailPage.hooks';
import { TransmittalCreationForm, ITransmittalCreationFormProps } from './TransmittalCreationForm';
import { transmittalCreationFormKeys } from './transmittalCreationForm.models';

export interface IUseTransmittalCreationModalProps {
  projectId: number;
  onSuccess?: (type?: ETransmittalType) => void;
}

export interface IRenderTransmittalCreationModalProps
  extends Omit<
    ITransmittalCreationFormProps,
    | 'projectId'
    | 'useFormMethods'
    | 'isCreateLoading'
    | 'isCreateError'
    | 'isCreateSuccess'
    | 'createDraftTransmittal'
    | 'createError'
  > {}

export const useTransmittalCreationModal = ({ projectId, onSuccess }: IUseTransmittalCreationModalProps) => {
  const { openPortal, closePortal, renderModal } = useModal();
  const { navigate } = useNavigate();
  const queryClient = useQueryClient();

  const useFormMethods = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
    defaultValues: {
      [transmittalCreationFormKeys.TYPE]: TransmittalTypeOptions[0],
    },
  });
  const {
    mutate: mutateCreateDraftTransmittal,
    data: dataCreateDraftTransmittal,
    error: errorCreateDraftTransmittal,
    reset,
    isLoading,
    isError,
    isSuccess,
  } = useCreateDraftProjectTransmittalIfNotExist();

  React.useEffect(() => {
    if (isSuccess && !!dataCreateDraftTransmittal?.item?.id) {
      closePortal();
      if (onSuccess) onSuccess(useFormMethods.getValues(transmittalCreationFormKeys.TYPE).code);
      navigate(getTransmittalItemRoute(dataCreateDraftTransmittal.item.type), {
        id: dataCreateDraftTransmittal?.item?.id.toString(),
      });
    }
  }, [isSuccess, dataCreateDraftTransmittal]);

  const generateCreateDraftTransmittalFn = (masterScheduleId: number, siteTransmittalId?: number) => {
    return (data: any) => {
      const createRequest: ICreateProjectTransmittalRequest = {
        projectId,
        masterScheduleId,
        type: data[transmittalCreationFormKeys.TYPE].code,
        siteTransmittalId,
      };

      mutateCreateDraftTransmittal(createRequest);
    };
  };

  const openTransmittalCreationModal = (defaultTransmittalType?: ETransmittalType) => {
    queryClient.invalidateQueries([
      projectId,
      FETCH_QUERIES_NAME.MASTER_SCHEDULE_OPTIONS_FOR_TRANSMITTAL,
      defaultTransmittalType,
    ]);
    useFormMethods.reset();
    const defaultTypeOption = !defaultTransmittalType
      ? null
      : TransmittalTypeOptions.find((tto) => tto.code === defaultTransmittalType);
    useFormMethods.setValue(transmittalCreationFormKeys.TYPE, defaultTypeOption || TransmittalTypeOptions[0]);
    reset();
    openPortal(NULL_OPEN_PORTAL_EVENT);
  };

  const renderTransmittalCreationModal = ({
    ...transmittalCreationFormProps
  }: IRenderTransmittalCreationModalProps) => {
    return renderModal({
      large: true,
      title: `Create New Transmittal`,
      onRenderBody: (bodyExtras?: ModalBodyExtras) => {
        return (
          <TransmittalCreationForm
            useFormMethods={useFormMethods}
            isCreateLoading={isLoading}
            isCreateError={isError}
            isCreateSuccess={isSuccess}
            createError={errorCreateDraftTransmittal}
            projectId={projectId}
            createDraftTransmittal={(masterScheduleId: number, siteTransmittalId?: number) => {
              const createDraftFn = generateCreateDraftTransmittalFn(masterScheduleId, siteTransmittalId);
              useFormMethods.handleSubmit(createDraftFn)();
            }}
            {...transmittalCreationFormProps}
          />
        );
      },
      onRenderFooter: (onHide?: () => void, bodyExtras?: ModalBodyExtras) => {
        return (
          <Button
            prefixIconName="ChevronLeft"
            type={ComponentType.Secondary}
            onClick={() => {
              if (onHide) {
                onHide();
              }
            }}
            style={{ margin: '10px' }}
          >
            Cancel
          </Button>
        );
      },
    });
  };
  return {
    openPortal: openTransmittalCreationModal,
    closePortal,
    renderModal: renderTransmittalCreationModal,
  };
};
